import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type {
  BookingReservationDetail,
  ClientOption,
} from '@/types/api/queries';

import {
  BookingDialogBody,
  BookingDialogFooter,
  ClientAutoComplete,
} from '@/components';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import { bookingCoverageCandidateQueries } from '@/queries';

import { useAttachBookingReservationMutation } from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

import { CoverageChoice, NO_COVERAGE } from './CoverageChoice';

type AttachReservationFormProps = {
  reservation: BookingReservationDetail;
  reservationsUrl: string;
  clientsSearchUrl: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const AttachReservationForm = (props: AttachReservationFormProps) => {
  const { reservation, reservationsUrl, clientsSearchUrl, onClose, onSuccess } =
    props;
  const { t } = useTranslation();

  const [pickedClient, setPickedClient] = useState<ClientOption | null>(
    reservation.client,
  );
  const [coverage, setCoverage] = useState<string>(NO_COVERAGE);
  const [isUsedNow, setIsUsedNow] = useState(true);

  const clientId = pickedClient?.id ?? null;

  const onClientChange = useCallback((client: ClientOption | null) => {
    setPickedClient(client);
    setCoverage(NO_COVERAGE);
  }, []);

  const { data: candidates, isLoading } = useQuery(
    bookingCoverageCandidateQueries.list({
      url: reservationsUrl,
      reservationId: reservation.id,
      clientId,
    }),
  );

  const { mutate: attach, isPending } = useAttachBookingReservationMutation();

  const coverageData = useMemo(() => {
    const [kind, rawId] = coverage.split(':');
    if (kind === 'card') {
      return { client_card_id: Number(rawId) };
    }
    if (kind === 'service') {
      return { client_service_id: Number(rawId) };
    }
    return {};
  }, [coverage]);

  const onConfirm = useCallback(() => {
    attach(
      {
        url: `${reservationsUrl}/${reservation.id}`,
        data: { client_id: clientId, use_now: isUsedNow, ...coverageData },
      },
      {
        onSuccess: (response) => {
          notifyBookingSuccess(response);
          onSuccess();
          onClose();
        },
        onError: notifyMutationError,
      },
    );
  }, [
    attach,
    clientId,
    coverageData,
    isUsedNow,
    onClose,
    onSuccess,
    reservation.id,
    reservationsUrl,
  ]);

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Attach')}</p>

        <ClientAutoComplete
          fetchUrl={clientsSearchUrl}
          queryKey={['booking_attach_client']}
          label={t('Who attended')}
          value={pickedClient}
          onChange={onClientChange}
        />

        {!clientId && (
          <p className="text-muted-foreground text-sm">
            {t('A place hold is marked attended without naming anyone')}
          </p>
        )}

        <CoverageChoice
          candidates={candidates}
          isLoading={isLoading}
          value={coverage}
          onChange={setCoverage}
        />

        <div className="flex items-center gap-2">
          <Checkbox
            id="booking-attach-use-now"
            checked={isUsedNow}
            onCheckedChange={(checked) => setIsUsedNow(checked === true)}
          />
          <Label
            htmlFor="booking-attach-use-now"
            className="font-normal"
          >
            {t('Use immediately')}
          </Label>
        </div>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Confirm')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onConfirm}
      />
    </>
  );
};
